import { CloudSync } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type ParserKey } from "@noa/api-core";
import { useTriggerIngestion } from "@noa/api-client";
import { MaximoSyncAllCard } from "./maximo-sync-all-card";
import { MaximoPresetCard } from "./maximo-preset-card";
import { restoreWhenLeftEmpty } from "~/utils/form-utils";
import { PARSER_OPTIONS, SYNC_PRESETS, type MaximoDocType, type SyncPreset } from "~/utils/maximo-sync";
import { Alert, Button, Card, Collapse, message, NumberField, Select, Switch, Text, Title } from "~/components/ui";

const DEFAULT_MAX_RECORDS = 10;

type MaximoSyncPanelProps = {
  onNavigateToHistory?: () => void;
};

export function MaximoSyncPanel({ onNavigateToHistory }: MaximoSyncPanelProps) {
  const { t } = useTranslation("admin");
  const [lastTaskId, setLastTaskId] = useState<string | null>(null);
  const [syncingType, setSyncingType] = useState<MaximoDocType | "all" | null>(null);
  const [withVision, setWithVision] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Advanced options
  const [skipExisting, setSkipExisting] = useState(false);
  const [parser, setParser] = useState<ParserKey | null>(null);
  const [limitEnabled, setLimitEnabled] = useState(false);
  const [maxRecords, setMaxRecords] = useState<number>(DEFAULT_MAX_RECORDS);

  const triggerSync = useTriggerIngestion();

  /** Body shared by the single-preset and sync-all paths. */
  const syncBody = (schemaId: string, useVision: boolean) => ({
    files: ["*"],
    source_type: "maximo" as const,
    schema_id: schemaId || undefined,
    with_vision: useVision,
    skip_existing: skipExisting,
    parser: parser || undefined,
    max_records: limitEnabled ? maxRecords : undefined,
  });

  const handleSyncPreset = (preset: SyncPreset) => {
    setSyncingType(preset.key);
    const useVision = preset.defaultVision ? withVision : false;
    triggerSync.mutate(syncBody(preset.schemaId, useVision), {
      onSuccess: (data) => {
        setLastTaskId(data.task_id);
        setSyncingType(null);
        setError(null);
        void message.success(t("uploadAdmin.sync.maximo.started"));
      },
      onError: () => {
        setSyncingType(null);
        setError(t("uploadAdmin.sync.maximo.error"));
      },
    });
  };

  const handleSyncAll = async () => {
    setSyncingType("all");
    const results: string[] = [];

    for (const preset of SYNC_PRESETS) {
      try {
        const useVision = preset.defaultVision ? withVision : false;
        // mutateAsync (not the raw endpoint) so each trigger invalidates the
        // job list — the loop used to bypass React Query entirely.
        const data = await triggerSync.mutateAsync(syncBody(preset.schemaId, useVision));
        results.push(`${preset.key}: ${data.task_id}`);
      } catch {
        void message.error(t("uploadAdmin.sync.maximo.errorType", { type: preset.key }));
      }
    }

    setSyncingType(null);
    if (results.length > 0) {
      setLastTaskId(results.join("\n"));
      void message.success(t("uploadAdmin.sync.maximo.startedAll", { count: results.length }));
    }
  };

  const advancedItems = [
    {
      key: "advanced",
      label: t("uploadAdmin.advanced.title"),
      children: (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span className="text-xs text-noa-muted block mb-1">{t("uploadAdmin.advanced.parser")}</span>
            <Select
              value={parser}
              onChange={setParser}
              options={[...PARSER_OPTIONS]}
              placeholder={t("uploadAdmin.form.parserPlaceholder")}
              allowClear
              className="w-full"
              size="small"
            />
          </div>
          <div className="flex items-center gap-2 pt-4">
            <Switch
              size="small"
              checked={skipExisting}
              onChange={setSkipExisting}
              aria-label={t("uploadAdmin.advanced.skipExisting")}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.skipExisting")}</span>
          </div>
          <div className="flex items-center gap-2 pt-4">
            <Switch
              size="small"
              checked={limitEnabled}
              onChange={setLimitEnabled}
              aria-label={t("uploadAdmin.advanced.maxRecords")}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.maxRecords")}</span>
            {limitEnabled && (
              <NumberField
                value={maxRecords}
                onChange={(v) => v !== null && setMaxRecords(v)}
                onBlur={restoreWhenLeftEmpty(DEFAULT_MAX_RECORDS, setMaxRecords)}
                min={1}
                max={10000}
                size="small"
                className="w-20"
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <CloudSync size={30} className="text-blue-500" />
        <div>
          <Title level={4} className="mb-0">
            {t("uploadAdmin.sync.maximo.title")}
          </Title>
          <Text tone="muted">{t("uploadAdmin.sync.maximo.description")}</Text>
        </div>
      </div>

      <MaximoSyncAllCard
        withVision={withVision}
        onWithVisionChange={setWithVision}
        syncingType={syncingType}
        onSyncAll={handleSyncAll}
      />

      {/* Advanced Options */}
      <Collapse items={advancedItems} size="small" />

      {/* Preset Cards — CSS Grid for equal heights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
        {SYNC_PRESETS.map((preset) => (
          <MaximoPresetCard
            key={preset.key}
            preset={preset}
            withVision={withVision}
            onWithVisionChange={setWithVision}
            syncingType={syncingType}
            onSync={handleSyncPreset}
          />
        ))}
      </div>

      {/* Error message */}
      {error && (
        <Alert
          type="error"
          message={t("common:error")}
          description={error}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* Success message — dark mode compatible */}
      {lastTaskId && (
        <Alert
          type="success"
          message={t("uploadAdmin.sync.taskStarted")}
          description={
            <div className="flex flex-col gap-2">
              <Text>
                {t("uploadAdmin.sync.taskId")}:{" "}
                <code className="bg-noa-hover px-2 py-0.5 rounded text-sm">{lastTaskId}</code>
              </Text>
              {onNavigateToHistory ? (
                <Button variant="link" size="small" className="!p-0 !h-auto" onClick={onNavigateToHistory}>
                  {t("uploadAdmin.sync.maximo.checkJobHistory")} →
                </Button>
              ) : (
                <Text tone="muted" className="text-sm">
                  {t("uploadAdmin.sync.maximo.checkJobHistory")}
                </Text>
              )}
            </div>
          }
          showIcon
          closable
          onClose={() => setLastTaskId(null)}
        />
      )}

      {/* How it works */}
      <Card title={t("uploadAdmin.sync.maximo.howItWorks")} size="small">
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 list-decimal pl-5 text-noa-muted">
          <li>{t("uploadAdmin.sync.maximo.step1")}</li>
          <li>{t("uploadAdmin.sync.maximo.step2")}</li>
          <li>{t("uploadAdmin.sync.maximo.step3")}</li>
          <li>{t("uploadAdmin.sync.maximo.step4")}</li>
        </ol>
      </Card>
    </div>
  );
}
